/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, forwardRef, useRef } from 'react';
import {
  FormControl,
  InputAdornment,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  OutlinedInput,
  Box,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { sortBy, throttle } from 'lodash';

export const SelectCheckbox = forwardRef(
  (
    {
      value = [],
      onChange = () => {},
      optionsData = [],
      optionName = 'name',
      fetchData = () => {},
      hasMore = false,
      loading = false,
      disabled = false,
      error = '',
      ...props
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const menuRef = useRef();

    const noneOption = useMemo(() => ({ id: 'select-none', [optionName]: 'Select None' }), [optionName]);
    const sortedOptions = useMemo(() => [noneOption, ...sortBy(options, [optionName])], [options, optionName]);

    useEffect(() => {
      setOptions(optionsData);
    }, [optionsData]);

    const handleSelectionChange = (event) => {
      const selected = event.target.value;
      const lastSelected = selected[selected.length - 1];

      if (lastSelected === 'select-none') {
        onChange([]);
        return;
      }

      onChange(selected.filter((id) => id !== 'select-none'));
    };

    // Infinite scroll handling
    const throttledFetchData = useMemo(
      () =>
        throttle(() => {
          if (!loading && hasMore) fetchData();
        }, 500),
      [fetchData, loading, hasMore]
    );

    const handleScroll = (event) => {
      const listNode = event.currentTarget;
      const bottomReached = listNode.scrollTop + listNode.clientHeight >= listNode.scrollHeight - 50;
      if (bottomReached) throttledFetchData();
    };

    return (
      <FormControl
        fullWidth
        size="small"
        error={!!error}
        sx={{ width: 230, maxWidth: 230 }}
      >
        <Select
          multiple
          disabled={disabled}
          value={value}
          onChange={handleSelectionChange}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => setOpen(false)}
          input={
            <OutlinedInput
              ref={ref}
              endAdornment={
                loading ? (
                  <InputAdornment
                    position="end"
                    sx={{ mr: '2px' }}
                  >
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null
              }
            />
          }
          displayEmpty
          IconComponent={loading ? () => null : KeyboardArrowDownIcon}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return 'Select Suppliers';
            }
            const selectedNames = options.filter((opt) => selected.includes(opt.id)).map((opt) => opt[optionName]);

            return (
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectedNames.join(', ')}
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              style: { maxHeight: 240 },
              onScroll: handleScroll,
              ref: menuRef,
            },
          }}
          {...props}
        >
          {sortedOptions.map((option) => {
            const checked = option.id === 'select-none' ? value.length === 0 : value.includes(option.id);

            return (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{
                  py: 0.25,
                  px: 1,
                  minHeight: 32,
                  fontSize: '0.85rem',
                }}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  sx={{ p: 0.5, mr: 0.5 }}
                />
                <ListItemText
                  primary={option[optionName]}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                  }}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  }
);

SelectCheckbox.displayName = 'SelectCheckbox';
